import React, { useEffect, useContext } from "react";
import { Box, Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { VideoCallContext } from "../context/VideoCallContext";
import {
  useTrackToggle,
  useMediaDevices,
  useMediaDeviceSelect,
  usePersistentUserChoices,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  ScreenShareIcon,
  ScreenShareOffIcon,
  PhoneOffIcon,
  PictureInPictureIcon,
  Maximize,
  Minimize,
  MessageSquare,
} from "lucide-react";
import MicrophoneButton from "./MicrophoneButton";
import VideoButton from "./VideoButton";

const VideoControlBar: React.FC<{
  commentsOpen: boolean;
  onToggleComments: () => void;
}> = ({ commentsOpen, onToggleComments }) => {
  const { manualEndVideoCall, videoCallState } = useContext(VideoCallContext);
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const {
    userChoices,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
    saveAudioInputEnabled,
    saveVideoInputEnabled,
  } = usePersistentUserChoices({
    defaults: {
      audioDeviceId: "",
      videoDeviceId: "",
      audioEnabled: true,
      videoEnabled: true,
      username: "User",
    },
  });

  const { toggle: toggleMic, enabled: micEnabled } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const { toggle: toggleCamera, enabled: cameraEnabled } = useTrackToggle({
    source: Track.Source.Camera,
  });

  const handleMicToggle = () => {
    toggleMic();
    // Save the new state to persistent storage
    saveAudioInputEnabled(!micEnabled);
  };
  const handleCameraToggle = () => {
    toggleCamera();
    // Save the new state to persistent storage
    saveVideoInputEnabled(!cameraEnabled);
  };
  const { toggle: toggleScreenShare, enabled: screenShareEnabled } =
    useTrackToggle({
      source: Track.Source.ScreenShare,
    });

  const audioDevices = useMediaDevices({ kind: "audioinput" });
  const videoDevices = useMediaDevices({ kind: "videoinput" });

  const {
    activeDeviceId: activeAudioDeviceId,
    setActiveMediaDevice: setActiveAudioDevice,
  } = useMediaDeviceSelect({ kind: "audioinput" });
  const {
    activeDeviceId: activeVideoDeviceId,
    setActiveMediaDevice: setActiveVideoDevice,
  } = useMediaDeviceSelect({ kind: "videoinput" });

  // Apply stored device preferences when devices are available
  useEffect(() => {
    if (userChoices.audioDeviceId && audioDevices.length > 0) {
      const isDeviceAvailable = audioDevices.some(
        (device) => device.deviceId === userChoices.audioDeviceId,
      );
      if (
        isDeviceAvailable &&
        activeAudioDeviceId !== userChoices.audioDeviceId
      ) {
        setActiveAudioDevice(userChoices.audioDeviceId);
      }
    }
  }, [
    audioDevices,
    userChoices.audioDeviceId,
    activeAudioDeviceId,
    setActiveAudioDevice,
  ]);

  useEffect(() => {
    if (userChoices.videoDeviceId && videoDevices.length > 0) {
      const isDeviceAvailable = videoDevices.some(
        (device) => device.deviceId === userChoices.videoDeviceId,
      );
      if (
        isDeviceAvailable &&
        activeVideoDeviceId !== userChoices.videoDeviceId
      ) {
        setActiveVideoDevice(userChoices.videoDeviceId);
      }
    }
  }, [
    videoDevices,
    userChoices.videoDeviceId,
    activeVideoDeviceId,
    setActiveVideoDevice,
  ]);

  // Apply stored enabled/disabled state when tracks are ready and differ from stored preferences
  useEffect(() => {
    if (
      userChoices.audioEnabled !== undefined &&
      micEnabled !== userChoices.audioEnabled
    ) {
      toggleMic();
    }
  }, [userChoices.audioEnabled, micEnabled]); // Remove toggleMic to prevent loops

  useEffect(() => {
    if (
      userChoices.videoEnabled !== undefined &&
      cameraEnabled !== userChoices.videoEnabled
    ) {
      toggleCamera();
    }
  }, [userChoices.videoEnabled, cameraEnabled]); // Remove toggleCamera to prevent loops

  const handleEndCall = () => {
    // Use the manual end function to set the flag
    manualEndVideoCall();
  };

  const handleBackToCall = () => {
    if (videoCallState.activeVideoRoom) {
      navigate(`/video/${videoCallState.activeVideoRoom.uuid}`);
    }
  };

  const handleFullScreenToggle = async () => {
    const videoContainer = document.querySelector(".lk-video-conference");
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      await videoContainer.requestFullscreen();
      setIsFullScreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  const isPipMode = videoCallState.renderLocation === "pip";
  const supportsScreenShare =
    typeof navigator?.mediaDevices?.getDisplayMedia === "function";

  return (
    <Box
      className="video-control-bar"
      sx={{
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {isPipMode && (
        <Tooltip title="Back to video page with notes">
          <IconButton
            onClick={handleBackToCall}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: "#636365",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <PictureInPictureIcon size={20} />
          </IconButton>
        </Tooltip>
      )}

      {/* Microphone Button */}
      <MicrophoneButton
        enabled={micEnabled}
        onToggle={handleMicToggle}
        devices={audioDevices}
        activeDeviceId={activeAudioDeviceId}
        onDeviceChange={(deviceId) => {
          setActiveAudioDevice(deviceId);
          saveAudioInputDeviceId(deviceId);
        }}
      />

      {/* Video Button */}
      <VideoButton
        enabled={cameraEnabled}
        onToggle={handleCameraToggle}
        devices={videoDevices}
        activeDeviceId={activeVideoDeviceId}
        onDeviceChange={(deviceId) => {
          setActiveVideoDevice(deviceId);
          saveVideoInputDeviceId(deviceId);
        }}
      />

      {/* Screen Share */}
      {supportsScreenShare && (
        <Box
          onClick={() => toggleScreenShare()}
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: screenShareEnabled ? "primary.main" : "#636365",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "white",
            "&:hover": {
              backgroundColor: screenShareEnabled
                ? "primary.dark"
                : "rgba(255, 255, 255, 0.2)",
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {screenShareEnabled ? (
            <ScreenShareOffIcon size={20} />
          ) : (
            <ScreenShareIcon size={20} />
          )}
        </Box>
      )}

      {!isPipMode && (
        <Tooltip title={commentsOpen ? "Hide comments" : "Show comments"}>
          <IconButton
            onClick={onToggleComments}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: commentsOpen ? "primary.main" : "#636365",
              color: "white",
              "&:hover": {
                backgroundColor: commentsOpen
                  ? "primary.dark"
                  : "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.05)",
              },
              transition: "all 0.2s ease",
            }}
          >
            <MessageSquare size={20} />
          </IconButton>
        </Tooltip>
      )}

      {/* Full Screen Toggle */}
      <Tooltip title={isFullScreen ? "Exit full screen" : "Full screen"}>
        <IconButton
          onClick={handleFullScreenToggle}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: isFullScreen ? "primary.main" : "#636365",
            color: "white",
            "&:hover": {
              backgroundColor: isFullScreen
                ? "primary.dark"
                : "rgba(255, 255, 255, 0.2)",
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
        >
          {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
        </IconButton>
      </Tooltip>

      {/* Leave Button */}
      <Box
        component="button"
        onClick={handleEndCall}
        sx={{
          backgroundColor: "secondary.dark",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PhoneOffIcon size={20} />
      </Box>
    </Box>
  );
};

export default VideoControlBar;
